import {Sensor, Measure} from "@core/model";
import {useThemeContext} from "@theme";
import {translate} from "@util";
import dayjs from "dayjs";
import {ChartsReferenceLine, LineSeriesType, LineChart} from "@mui/x-charts";
import {Multivalue} from "@core/model/Measure";
import {useEffect, useState} from "react";
import {SeriesToggle} from "./SeriesToggle";
import {format} from "@constants";
import {ChartMargin} from "@mui/x-charts/internals/plugins/corePlugins/useChartDimensions/useChartDimensions.types";
import {
  amber,
  blue,
  brown,
  cyan,
  deepOrange,
  blueGrey,
  deepPurple,
  green,
  indigo,
  lightBlue,
  lightGreen,
  lime,
  orange,
  pink,
  purple,
  red,
  teal,
  yellow,
} from "@mui/material/colors";

const colors = [
  amber,
  blue,
  brown,
  cyan,
  deepOrange,
  blueGrey,
  deepPurple,
  green,
  indigo,
  lightBlue,
  lightGreen,
  lime,
  orange,
  pink,
  purple,
  red,
  teal,
  yellow,
];

// Le misure possono contenere stringhe: le numeriche vengono convertite,
// le altre diventano null (punto vuoto sul grafico)
const toChartValue = (value: unknown): number | null => {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string" && value.trim() !== "") {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }
  return null;
};

const MeasureChart = ({
  measures = [],
  sensor,
  live,
  height,
  margin = {top: 16, right: 32, left: 8, bottom: 16},
  seriesSelector = "menu",
}: {
  measures: Measure<number | Multivalue>[] | undefined;
  sensor: Sensor;
  live?: boolean;
  height?: number;
  margin?: ChartMargin;
  seriesSelector?: "buttons" | "menu";
}) => {
  const {theme} = useThemeContext();

  const [series, setSeries] = useState<LineSeriesType[]>([]);
  const [visibleSeries, setVisibileSeries] = useState<LineSeriesType[]>([]);
  const [xAxis, setXAxis] = useState<number[]>([]);

  const charProps: LineSeriesType = {
    curve: "linear",
    showMark: false,
    type: "line",
  };

  useEffect(() => {
    if (measures.length === 0) return;

    const xAxis = measures.map((_m, index) => index + 1);
    setXAxis(xAxis);
    // Nel tooltip i valori non rappresentabili vengono mostrati come testo
    const buildSeries = (
      id: string,
      color: string,
      rawValues: unknown[],
    ): LineSeriesType => ({
      id,
      label: id,
      color,
      data: rawValues.map(toChartValue),
      valueFormatter: (value, {dataIndex}) => {
        if (value !== null) return value.toLocaleString();
        const raw = rawValues[dataIndex];
        return raw === null || raw === undefined || raw === ""
          ? null
          : String(raw);
      },
      ...charProps,
    });

    let newSeries: LineSeriesType[] = [];
    if (sensor.type === "SENSOR_SCALAR") {
      newSeries = [
        buildSeries(
          sensor.name,
          theme.colors.tertiary,
          measures.map(m => m.value),
        ),
      ];
    } else {
      const columns = sensor.columns ? sensor.columns.split(",") : [];
      const lines =
        columns.length > 0 ? columns : Object.keys(measures[0].value || {});
      const linesWithoutTs = [...lines].splice(1);
      newSeries = linesWithoutTs.map((line, index) =>
        buildSeries(
          line,
          colors[index % colors.length][500],
          measures.map(m => (m.value as Multivalue)?.[index + 1]),
        ),
      );
    }
    setSeries(newSeries);
    setVisibileSeries(newSeries);
  }, [measures]);

  const handleToggleSeries = (selection: string[]) => {
    const newVisibleSeries = series.filter(
      s => selection.indexOf(s.id!.toString()) !== -1,
    );
    setVisibileSeries(newVisibleSeries);
  };

  const dateFormatter = (index: number, context: {location: string}) => {
    if (!measures[index - 1]) return "";
    const ts = measures[index - 1].timestamp;

    if (context.location === "tick") {
      return dayjs(ts).format(format.time.short);
    }
    return dayjs(ts).format(format.datetime.long);
  };

  return (
    <>
      <SeriesToggle
        series={series}
        selection={visibleSeries.map(s => s.id!.toString())}
        onChange={handleToggleSeries}
        layout={seriesSelector}
      />
      <LineChart
        margin={margin}
        series={visibleSeries}
        height={height}
        xAxis={[
          {scaleType: "point", data: xAxis, valueFormatter: dateFormatter},
        ]}
        skipAnimation={live}
        grid={{vertical: true, horizontal: true}}
        yAxis={[{width: 40, min: sensor.min, max: sensor.max}]}
        hideLegend
        localeText={{
          noData: translate("common.table.noRowsLabel"),
        }}>
        {sensor.thresholdMin && (
          <ChartsReferenceLine
            y={sensor.thresholdMin}
            label={translate("entity.sensor.thresholdMin")}
            lineStyle={{stroke: "red"}}
          />
        )}
        {sensor.thresholdMax && (
          <ChartsReferenceLine
            y={sensor.thresholdMax}
            label={translate("entity.sensor.thresholdMax")}
            lineStyle={{stroke: "red"}}
          />
        )}
      </LineChart>
    </>
  );
};

export {MeasureChart};
